import { useEffect, useRef, useState } from 'react';
import { Message } from '../../../types';
import { scrollToBottom, isScrolledToBottom } from '../../../lib/utils';
import MessageItem from './MessageItem';
import TypingIndicator from './TypingIndicator';
import { useSocketEvent, SOCKET_EVENTS } from '../../../lib/websocket';

interface MessageListProps {
  messages: Message[];
  conversationId: string;
  hasMore?: boolean;
  loadMore?: () => void;
  loadingMore?: boolean;
  onEditMessage?: (messageId: string, content: string) => Promise<void> | void;
  onDeleteMessage?: (messageId: string) => Promise<void> | void;
}

export default function MessageList({
  messages,
  conversationId,
  hasMore,
  loadMore,
  loadingMore,
  onEditMessage,
  onDeleteMessage,
}: MessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isTyping, setIsTyping] = useState(false);

  // Listen for typing events
  useSocketEvent(SOCKET_EVENTS.STAFF_TYPING, (data) => {
    if (data.conversationId === conversationId) {
      setIsTyping(true);
    }
  });

  useSocketEvent(SOCKET_EVENTS.STAFF_STOP_TYPING, (data) => {
    if (data.conversationId === conversationId) {
      setIsTyping(false);
    }
  });

  // Force-scroll after conversation switch or initial load
  const shouldScrollRef = useRef(true);

  useEffect(() => {
    shouldScrollRef.current = true;
  }, [conversationId]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || messages.length === 0) return;

    if (shouldScrollRef.current) {
      scrollToBottom(container, 'auto');
      shouldScrollRef.current = false;
    } else if (isScrolledToBottom(container)) {
      scrollToBottom(container);
    }
  }, [messages]);

  return (
    <div
      ref={containerRef}
      className="flex-1 space-y-4 overflow-y-auto p-6 scrollbar-thin"
    >
      {/* Load more button */}
      {hasMore && (
        <div className="flex justify-center pb-2">
          <button
            onClick={loadMore}
            disabled={loadingMore}
            className="rounded-full border border-line bg-surface px-4 py-1.5 text-xs font-medium text-ink-2 hover:bg-black/[0.04] disabled:opacity-50"
          >
            {loadingMore ? 'Loading...' : 'Load older messages'}
          </button>
        </div>
      )}

      {messages.map((message) => (
        <MessageItem
          key={message.id}
          message={message}
          onEdit={onEditMessage}
          onDelete={onDeleteMessage}
        />
      ))}
      {isTyping && <TypingIndicator />}
      <div ref={messagesEndRef} />
    </div>
  );
}
